/* @flow */

import * as React from 'react';
import { observer } from 'mobx-react';
import { observable, computed, action, autorun } from 'mobx';
import { Link } from 'react-router-dom';
import { translate } from 'react-i18next';
import { ReactAccessConsumer } from 'react-access';
import moment from 'moment';
import Button from '@material-ui/core/Button';

import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';

import ElectroDatePicker from 'forms/components/inputs/ElectroDatePicker/ElectroDatePicker';
import { getActivity } from 'components/Activity/ActivityData';
import Loading from 'components/Loading/Loading';
import { apiService, errorHandlerService } from 'services/ServiceRegistry';
import activityStyles from 'components/Activity/Activity.module.scss';
import styles from 'common-scss/DetailsActivity.module.scss';
import { Roles } from 'permissions/defs';

export class ActivityModel {
  @observable endpoint: ?string;

  @observable activity: ?Array<*>;

  @observable loading: boolean = false;

  @observable filter: Object = {
    page_size: 1000, // eslint-disable-line camelcase
    date_from: moment(new Date())
      .subtract(30, 'days')
      .format('YYYY-MM-DD'), // eslint-disable-line camelcase
    date_to: moment(new Date()).format('YYYY-MM-DD'), // eslint-disable-line camelcase
    search: null,
  };

  @observable activityLngData: Object = {};

  constructor(props: *) {
    this.url = props.url;

    // check if translate file, for this language, exists
    // then get all translated activities
    if (props.i18n.services.resourceStore.hasResourceBundle(props.i18n.language, 'general')) {
      const generalLngData = props.i18n.services.resourceStore.getResource(
        props.i18n.language,
        'general'
      );
      this.activityLngData =
        generalLngData && generalLngData.activities ? generalLngData.activities : {};
    }
  }

  @computed get filterString(): string {
    return Object.entries(this.filter)
      .map(([key: string, val: *]: Array<*>) => (val ? `${key}=${val}` : null))
      .filter(Boolean)
      .join('&');
  }

  @computed get filteredActivities(): * {
    if (this.activity) {
      const array: * = this.activity.data;
      return array.map((obj: Object) => getActivity(obj));
    }

    return null;
  }

  @action
  async loadActivity(): Promise<*> {
    if (this.url) {
      this.endpoint = this.url;
    }

    if (!this.loading && this.endpoint) {
      this.loading = true;

      // unsuccessful means that the translate file is searched, but nothing found
      if (this.filter.search === 'unsuccessful') {
        this.activity.data = [];
      } else {
        try {
          this.activity = await apiService.getResource(this.endpoint + '?' + this.filterString);
        } catch (e) {
          errorHandlerService.handleException(e);
        }
      }

      this.loading = false;
    }
  }

  reset() {
    this.endpoint = null;
    this.filter.date_to = null; // eslint-disable-line camelcase
    this.filter.date_from = null; // eslint-disable-line camelcase
    this.filter.search = null;
    this.filter.page_size = 1000; // eslint-disable-line camelcase
    this.activity = null;
  }
}

@translate()
@observer
export default class Activity extends React.Component<*> {
  @observable store: *;

  @observable searchString: string = '';

  componentDidMount() {
    this.store = new ActivityModel(this.props);
    this.store.filter.page_size = this.props.maxResults // eslint-disable-line camelcase
      ? this.props.maxResults
      : 1000;
    this.store.loadActivity();

    autorun(() => {
      const to: * = this.store.filter.date_to;
      const from: * = this.store.filter.date_from;

      if ((to && from) || (!to && !from)) {
        this.store.loadActivity();
      }

      if (this.props.refresh) {
        this.store.loadActivity();
      }
    });
  }

  searchLanguageByString(strSearch): String {
    // if search string is empty return all activities
    if (strSearch === '') {
      return '';
    }

    const tempStr = Object.entries(this.store.activityLngData)
      .reduce((searchArr: Array<*>, trnlsArr: Array<*>) => {
        const key = trnlsArr[0].toLowerCase();
        const val = trnlsArr[1].toLowerCase();

        if (val.indexOf(strSearch.toLowerCase()) > -1) {
          searchArr.push(key);
        }

        return searchArr;
      }, [])
      .join('|');

    // if tempStr is empty means nothing found, so return 'unsuccessful'
    // no need to call the endpoint, set activity.data = []
    if (tempStr === '') {
      return 'unsuccessful';
    } else {
      return tempStr;
    }
  }

  render(): React.Element<*> {
    const t: Function = this.props.t;
    const activity: * = this.store ? this.store.filteredActivities : null;

    if (activity) {
      return (
        <div className={activityStyles['activity-wrapper']}>
          {this.props.allowFilter && (
            <div className={activityStyles['activity-inputs']}>
              {this.props.title && <h3>{t('general:activity')}</h3>}
              <form
                onSubmit={(e: Object) => {
                  e.preventDefault();
                  this.store.filter.search = this.searchString;
                  this.store.loadActivity();
                }}
              >
                <TextField
                  className={styles['search-input']}
                  id="margin-none"
                  placeholder={t('general:placeholders.search_activity')}
                  onChange={(e: SyntheticInputEvent<*>) => {
                    this.searchString = this.searchLanguageByString(e.target.value);
                  }}
                  InputProps={{
                    disableUnderline: true,
                    startAdornment: (
                      <InputAdornment
                        className={activityStyles['column-input-adornment']}
                        position="start"
                      >
                        <button type="submit">
                          <svg className={activityStyles['icon-search']}>
                            <use xlinkHref="#icon-search" />
                          </svg>
                        </button>
                      </InputAdornment>
                    ),
                  }}
                />
              </form>
              <h4>{t('general:labels.set_date_range')}</h4>
              {/* eslint-disable */}

              <ElectroDatePicker
                floatingLabelText={t('general:labels.from_date')}
                maxDate={
                  this.store.filter.date_to ? new Date(this.store.filter.date_to) : new Date()
                }
                value={this.store.filter.date_from ? new Date(this.store.filter.date_from) : null}
                onChange={(e: SyntheticInputEvent<*>, date: Date) => {
                  this.store.filter.date_from = moment(date).format('YYYY-MM-DD');
                }}
                clearDate={() => {
                  this.store.filter.date_from = null;
                }}
              />

              <ElectroDatePicker
                floatingLabelText={t('general:labels.to_date')}
                minDate={new Date(this.store.filter.date_from)}
                maxDate={new Date()}
                value={this.store.filter.date_to ? new Date(this.store.filter.date_to) : null}
                onChange={(e: SyntheticInputEvent<*>, date: Date) => {
                  this.store.filter.date_to = moment(date).format('YYYY-MM-DD');
                }}
                clearDate={() => {
                  this.store.filter.date_to = null;
                }}
              />
              {/* eslint-enable */}
            </div>
          )}
          {activity ? <ActivityList activities={activity} /> : <Loading />}
        </div>
      );
    }

    return <Loading />;
  }
}

export const ActivityList = translate()(({ activities, t }) => {
  return (
    <ul className={activityStyles.list}>
      {activities.length ? (
        activities.map((activity: Object, index) => {
          return <ActivityItem activity={activity} key={index} />;
        })
      ) : (
        <li className={activityStyles['list-item-no-data']}>
          {t('general:search_results.no_activity_found')}
        </li>
      )}
    </ul>
  );
});

export const ActivityItem = translate()(({ activity, t }) => {
  return (
    <li key={activity.id} className={activityStyles['list-item']}>
      <svg>
        <use xlinkHref={activity.icon} />
      </svg>
      <div>
        <h4>{t(`general:activities.${activity.tag}`)}</h4>
        <ReactAccessConsumer>
          {authorizeAccess => {
            return (
              <p>
                {authorizeAccess([Roles.SYSTEM_ADMIN]) ? (
                  <Link to={`/users/profile/${activity.userId}`}>{activity.user}</Link>
                ) : (
                  <Link to="#">{activity.user}</Link>
                )}
                {` - ${activity.date}`}
              </p>
            );
          }}
        </ReactAccessConsumer>
        {activity.can_review && (
          <div className={styles['button-container']}>
            <Link to={`/sent-requests/${activity.sent_request_id}`}>
              <Button variant="contained"> {t('general:actions.review')}</Button>
            </Link>
          </div>
        )}
      </div>
    </li>
  );
});
